<template>
    <div class="flex flex-col items-center text-center mb-16 min-[250px]">
        <div 
            class="flex gap-1 lg:gap-4 text-base lg:text-lg font-semibold items-center min-[250px]"
            :class="{
                'flex': showVerticalOnMobile,
                'flex-row': !showVerticalOnMobile
            }"
        >
            <span 
                v-if="esco" 
                @click="toggleModel('esco')" 
                class="cursor-pointer flex items-center transition uppercase duration-300 relative px-2 py-1 lg:py-0 w-full lg:w-auto justify-center lg:justify-start"
                :class="{
                'text-hwine font-bold': currentModel === 'esco',
                'text-gray-600 hover:text-hwine': currentModel !== 'esco'
                }"
            >
                Escolarizado
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="ml-1 transition-transform duration-200" :class="{ 'text-hwine': currentModel === 'esco', 'rotate-180': currentModel === 'esco' }">
                    <path d="M7 10l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-if="currentModel === 'esco'" class="absolute -bottom-1 left-0 right-0 h-0.5 bg-hwine rounded-full"></span>
            </span>
            
            <span v-if="esco && (ejec || virt)" class="text-gray-400 hidden lg:inline">•</span>

            <span 
                v-if="ejec" 
                @click="toggleModel('ejec')" 
                class="cursor-pointer flex items-center transition uppercase duration-300 relative px-2 py-1 lg:py-0 w-full lg:w-auto justify-center lg:justify-start"
                :class="{
                'text-hwine font-bold': currentModel === 'ejec',
                'text-gray-600 hover:text-hwine': currentModel !== 'ejec'
                }"
            >
                Ejecutivo
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="ml-1 transition-transform duration-200" :class="{ 'text-hwine': currentModel === 'ejec', 'rotate-180': currentModel === 'ejec' }">
                    <path d="M7 10l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-if="currentModel === 'ejec'" class="absolute -bottom-1 left-0 right-0 h-0.5 bg-hwine rounded-none"></span>
            </span>
            
            <span v-if="ejec && virt" class="text-gray-400 hidden lg:inline">•</span>

            <span 
                v-if="virt" 
                @click="toggleModel('virt')" 
                class="cursor-pointer flex items-center transition uppercase duration-300 relative px-2 py-1 lg:py-0 w-full lg:w-auto justify-center lg:justify-start"
                :class="{
                'text-hwine font-bold': currentModel === 'virt',
                'text-gray-600 hover:text-hwine': currentModel !== 'virt'
                }"
            >
                Virtual
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="ml-1 transition-transform duration-200" :class="{ 'text-hwine': currentModel === 'virt', 'rotate-180': currentModel === 'virt' }">
                    <path d="M7 10l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-if="currentModel === 'virt'" class="absolute -bottom-1 left-0 right-0 h-0.5 bg-hwine rounded-none"></span>
            </span>
        </div>

        <div class="mt-4 flex justify-center w-full min-[250px] h-full">
            <transition name="fade" mode="out-in">
                <div v-if="currentModel && imageSources" :key="currentModel" class="lg:w-[60%] w-full min-h-[250px] h-auto">
                    <picture>
                        <source media="(max-width: 1023px)" :srcset="imageSources.mobile" />
                        <img 
                            :src="imageSources.desktop" 
                            alt="Modelo seleccionado" 
                            class="w-full h-auto rounded-none shadow-lg border border-hgray" 
                        />
                    </picture>
                </div>
            </transition>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';

type ModelType = 'esco' | 'ejec' | 'virt';

export default defineComponent({
    props: {
        esco: { type: [String, Object, Array, Boolean, Number], default: null },
        ejec: { type: [String, Object, Array, Boolean, Number], default: null },
        virt: { type: [String, Object, Array, Boolean, Number], default: null },
        escoT: { type: Boolean, default: false },
        ejecT: { type: Boolean, default: false },
        virtT: { type: Boolean, default: false }
    },
    setup(props: any) {
        const currentModel = ref<ModelType | null>(null);

        // Función auxiliar para separar las rutas
        const parseImagePath = (rawPath: string | unknown) => {
            if (typeof rawPath !== 'string') return null;
            
            if (rawPath.includes(',')) {
                const parts = rawPath.split(',');
                return {
                    desktop: parts[0].trim(),
                    // Si existe la segunda parte, úsala, si no, usa la primera
                    mobile: parts[1] ? parts[1].trim() : parts[0].trim()
                };
            }
            
            // Si no hay coma, la misma imagen para ambos
            return {
                desktop: rawPath,
                mobile: rawPath
            };
        };

        const imageSources = computed(() => {
            if (!currentModel.value) return null;
            
            let rawData = null;

            switch (currentModel.value) {
                case 'esco': rawData = props.escoT ? props.esco : null; break;
                case 'ejec': rawData = props.ejecT ? props.ejec : null; break;
                case 'virt': rawData = props.virtT ? props.virt : null; break;
            }

            return parseImagePath(rawData);
        });

        const availableModels = computed(() => {
            return [props.esco, props.ejec, props.virt].filter(Boolean).length;
        });

        const showVerticalOnMobile = computed(() => {
            return availableModels.value > 2;
        });

        const toggleModel = (model: ModelType) => {
            if (currentModel.value === model) {
                currentModel.value = null;
            } else {
                currentModel.value = model;
            }
        };

        onMounted(() => {
            if (props.esco && props.escoT) {
                currentModel.value = 'esco';
            } else if (props.ejec && props.ejecT) {
                currentModel.value = 'ejec';
            } else if (props.virt && props.virtT) {
                currentModel.value = 'virt';
            }
        });

        return { 
            imageSources, 
            toggleModel, 
            currentModel,
            showVerticalOnMobile
        };
    }
});
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}

.bg-hwine {
    background-color: var(--hwine);
}

.rotate-180 {
    transform: rotate(180deg);
}
</style>